// A report batch's "Export MultiSite XLSX": the bills one bulk send covered, in
// the same MultiSite Bill Validation Detail Report layout as Bill Runs' export
// (lib/monthlyBillReport), plus its Summary sheet.

import type { BvBill } from '../types/billTypes';
import type { ExportSheet } from '../utils/exportUtils';
import type { BillReport } from './billVerdict';
import { isBvSite } from './billRunConditions';
import { detailRow, monthMetrics, summaryRows } from './monthlyBillReport';

const pad = (n: number) => String(n).padStart(2, '0');

/** "Batch 2026-10-28 14.05", local time; filename-safe (no colons or slashes). */
export function batchLabel(createdAt: string): string {
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return 'Batch';
  return `Batch ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}.${pad(d.getMinutes())}`;
}

export const batchReportFilename = (createdAt: string): string =>
  `MultiSite Bill Validation Detail Report - ${batchLabel(createdAt)}`;

/**
 * The bills in batch order (`billIds`, from the batch items' positions), each
 * once; bills not loaded (deleted since) and non-BV sites are left out.
 */
export function orderBatchBills<B extends Partial<BvBill> & { id?: string }>(billIds: string[], bills: B[]): B[] {
  const byId = new Map(bills.map((b) => [b.id, b]));
  const seen = new Set<string>();
  const out: B[] = [];
  for (const id of billIds) {
    const bill = byId.get(id);
    if (!bill || seen.has(id) || !isBvSite(bill.site)) continue;
    seen.add(id);
    out.push(bill);
  }
  return out;
}

/** Summary + Multiple Sites sheets for the batch's bills, judged by `reportFor`. */
export function batchMultiSiteSheets(
  label: string,
  billIds: string[],
  bills: Partial<BvBill>[],
  reportFor: (bill: Partial<BvBill>) => BillReport | null,
): ExportSheet[] {
  const rows = orderBatchBills(billIds, bills).map((bill) => ({ bill, report: reportFor(bill) }));
  // Bill Runs' Summary leads with the month; a batch is named by when it ran.
  const summary = summaryRows(label, monthMetrics(rows)).map((r, i) => (i === 0 ? { Metric: 'Batch', Value: label } : r));
  return [
    { name: 'Summary', rows: summary },
    { name: 'Multiple Sites', rows: rows.map((r) => detailRow(r.bill, r.report)) },
  ];
}
